import React, { useEffect, useRef, useState } from "react";
import {
  Search,
  Filter,
  X,
  Download,
  Eye,
  EyeOff,
  Settings,
  CheckSquare,
  Square,
  Minus,
  FileText,
  FileSpreadsheet,
} from "lucide-react";
import { DatePicker } from "antd";
import moment from "moment";
import ClientAutoComplete from "./ClientAutoComplete";
import { useParams } from "react-router-dom";
import useTenantNavigate from "../hooks/useTenantNavigate";

const { RangePicker } = DatePicker;

// Column filter dropdown. MUST be a module-level component (not defined
// inside TableV2's body). When it was nested inside TableV2, every parent
// re-render created a brand-new FilterDropdown function identity, so React
// unmounted+remounted it and its local `tempValue` state (the text the
// user is typing but hasn't Applied yet) was wiped back to null. List
// pages poll a fetch-status endpoint every ~2.5s and call setNotices() on
// each tick, which re-rendered TableV2 and cleared any in-progress filter
// input. Keeping the component identity stable across re-renders fixes the
// "filter auto-clears after a few seconds" bug on ALL list views.
// Closed-over parent values are now passed in as props.
const FilterDropdown = ({
  column,
  isOpen,
  onClose,
  columnIndex,
  totalColumns,
  activeFilters,
  handleFilterChange,
  serviceType,
  statusOptions = [],
}) => {
  const [tempValue, setTempValue] = useState(null);
  const dropdownRef = useRef(null);
  useEffect(() => {
    const currentValue = activeFilters[column.filterName];
    if (currentValue !== undefined && currentValue !== null) {
      setTempValue(currentValue);
    } else {
      setTempValue(null);
    }
  }, [activeFilters, column.filterName, isOpen]);

  if (!isOpen) return null;

  const applyFilter = () => {
    handleFilterChange(column.filterName, tempValue);
    onClose();
  };

  const clearFilter = () => {
    setTempValue(null);
    handleFilterChange(column.filterName, null);
    onClose();
  };

  const isLastColumns = columnIndex >= totalColumns - 2;

  const renderFilterInput = () => {
    switch (column.filterType || column.filterName) {
      case "autocomplete":
      case "client_name":
        return (
          <ClientAutoComplete
            serviceType={serviceType}
            value={tempValue}
            handleSelect={(value) => setTempValue(value)}
            style={{ zIndex: 2147483647 }}
            className="w-full h-10 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
          />
        );

      case "select":
      case "proceeding_status":
      case "notice_status":
        return (
          <select
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
          >
            <option value="">All {column.label}</option>
            {(column.filterOptions || statusOptions).map((option) => (
              <option
                key={option?.value || option}
                value={option?.value || option}
              >
                {option?.label || option}
              </option>
            ))}
          </select>
        );

      case "date":
      case "noticeSentDate":
      case "dueDate":
        return (
          <div className="space-y-2">
            <label className="text-xs text-gray-600 font-medium">
              Date Range
            </label>
            <RangePicker
              className="w-full"
              size="small"
              format="YYYY-MM-DD"
              value={
                Array.isArray(tempValue) && tempValue.length === 2
                  ? [tempValue[0], tempValue[1]]
                  : null
              }
              onChange={(dates) => {
                if (dates && dates.length === 2) {
                  setTempValue([dates[0], dates[1]]);
                } else {
                  setTempValue([]);
                }
              }}
              placeholder={["Start Date", "End Date"]}
              style={{ zIndex: 2147483647 }}
              popupStyle={{ zIndex: 2147483647 }}
            />
          </div>
        );

      default:
        return (
          <div className="relative">
            <input
              type="text"
              className="w-full px-3 py-2 pl-9 text-sm border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder={`Search ${column.label}...`}
              value={tempValue}
              onChange={(e) => setTempValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  applyFilter();
                }
              }}
            />
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
          </div>
        );
    }
  };

  return (
    <div
      ref={dropdownRef}
      onMouseEnter={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      onMouseLeave={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      onClick={(e) => {
        e.stopPropagation();
      }}
      className={`absolute top-full mt-1 w-80 bg-white border border-gray-200 rounded-xl shadow-2xl z-[99999] animate-in fade-in duration-200 ${
        isLastColumns ? "right-0" : "left-0"
      }`}
    >
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-gray-800 text-sm">
            Filter {column.label}
          </h4>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-3 h-3 text-gray-400" />
          </button>
        </div>

        <div className="space-y-3">
          {renderFilterInput()}

          <div className="flex items-center gap-2 pt-2">
            <button
              onClick={applyFilter}
              className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Apply
            </button>
            <button
              onClick={clearFilter}
              className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 transition-colors font-medium"
            >
              Clear
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const TableV2 = ({
  columns,
  title = "Notices",
  // Optional node rendered centered in the header's empty space (between the
  // title block and the action buttons). Omit for the default layout.
  headerCenter = null,
  data,
  limit = 10,
  limitStart = 0,
  totalRecords = 0,
  setLimitStart,
  onPageChange = () => {},
  // Optional: page-length selector + "View All" in the footer. When
  // provided, the footer renders a records-per-page <select> (20/50/100…)
  // and a "View All" button that loads every record on one page. Parent
  // owns `limit`; this just hands back the new size.
  onLimitChange = null,
  // DEPRECATED legacy CSV/PDF export icon in the toolbar. Off by default —
  // the unified DownloadDataButton in each page's actions row replaces it.
  showLegacyExport = false,
  rowRedirection,
  isPagination = 1,
  serviceType = null,
  isLoading = false,
  type = null,
  selectedIds = [],
  setSelectedIds = () => {},
  onFilterChange = () => {},
  filters = {},
  onExport = () => {},
  // Optional async fn returning ALL records matching current filters.
  // When provided, PDF/CSV button clicks fetch the full dataset
  // (parent loops its existing list endpoint in batches of 500).
  // When not provided, falls back to existing current-page export.
  onFetchAllRecords = null,
  filterValues = {},
  handleFilterValueChange = () => {},
  statusOptions = [],
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [activeFilters, setActiveFilters] = useState(filterValues || {});
  const [hoveredRow, setHoveredRow] = useState(null);
  const [openFilterDropdown, setOpenFilterDropdown] = useState(null);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [isExportingAll, setIsExportingAll] = useState(false);
  const navigate = useTenantNavigate();
  const { clientId } = useParams();

  useEffect(() => {
    const calculatedTotalPages = Math.ceil(totalRecords / limit) || 1;
    const calculatedCurrentPage = Math.floor(limitStart / limit) + 1;

    if (calculatedCurrentPage > calculatedTotalPages) {
      setLimitStart(0);
      setCurrentPage(1);
      setTotalPages(calculatedTotalPages);
    } else {
      setCurrentPage(calculatedCurrentPage);
      setTotalPages(calculatedTotalPages);
    }
  }, [limitStart, limit, totalRecords]);

  useEffect(() => {
    setActiveFilters(filterValues || {});
  }, [filterValues]);

  const handleRowClick = (data) => {
    if (rowRedirection && typeof rowRedirection === "function") {
      if (serviceType === "income-tax" && type === "clients") {
        navigate(`/${serviceType}/client-view/${data.id || data.name}`);
      } else if (serviceType === "tds" && type === "clients") {
        navigate(`/${serviceType}/client-view/${data.id || data.name}`);
      } else if (serviceType === "gstin" && type === "clients") {
        navigate(`/${serviceType}/client-view/${data.id || data.name}`);
      } else {
        navigate(rowRedirection(data));
      }
    } else if (rowRedirection && typeof rowRedirection === "string") {
      // Only pass clientId when navigating from a client-specific page (clientId in URL params)
      // When from Total Notices (no clientId in URL), omit it so breadcrumb routes back to Total Notices
      const clientParam = clientId ? `&clientId=${clientId}` : "";
      // Propagate the source filter (e.g. ?type=open_notices) from the
      // current list URL onto the detail URL so the detail page's
      // breadcrumb can show "Open Notices" instead of the generic
      // "E-Proceedings". Same pattern as `clientParam` above. Empty
      // string when navigating from a non-filtered list.
      const _params = new URLSearchParams(window.location.search);
      const _sourceType = _params.get("type");
      const typeParam = _sourceType
        ? `&type=${encodeURIComponent(_sourceType)}`
        : "";
      if (type === "eproceeding") {
        if (data?.notice_type !== "E Proceeding") {
          navigate(
            `${rowRedirection}?responseID=${
              data?.name
            }&formType=responseOutstandings${clientParam}&activeTab=response${typeParam}`
          );
        } else {
          navigate(
            `${rowRedirection}?proceedingID=${
              data?.name
            }&formType=eproceedings${clientParam}&activeTab=eproceeding${typeParam}`
          );
        }
      } else if (type === "failed_login") {
        navigate(
          `${rowRedirection}?failedLoginID=${
            data?.name
          }&formType=failedLogin${clientParam}${typeParam}`
        );
      } else if (type === "responseoutstandings") {
        navigate(
          `${rowRedirection}?responseID=${
            data?.name
          }&formType=responseOutstandings${clientParam}&activeTab=response${typeParam}`
        );
      } else if (type === "notices" && serviceType === "gstin") {
        if (data.notice_type == "Additional Notices") {
          navigate(
            `${rowRedirection}?additionalNoticeID=${
              data?.name
            }&formType=additionalNotices${clientParam}&activeTab=additional${typeParam}`
          );
        } else {
          navigate(
            `${rowRedirection}?noticeID=${
              data?.name
            }&formType=notices${clientParam}&activeTab=normal${typeParam}`
          );
        }
      } else if (type === "notices" && serviceType === "tds") {
        navigate(
          `${rowRedirection}?tdsNoticeID=${
            data?.name
          }&formType=tdsNotices${clientParam}${typeParam}`
        );
      } else {
        navigate(rowRedirection);
      }
    }
  };

  const DATE_DISPLAY_FMT = "DD MMMM, YYYY";

  const formatDateOrRange = (val) => {
    if (Array.isArray(val) && val.length === 2) {
      // AntD v5 RangePicker returns dayjs objects (not moment). Wrapping
      // them via `moment(dayjsObj)` does NOT clone the date — moment can't
      // recognise dayjs and silently creates a fresh moment for NOW. The
      // result is the chip rendered as today's date instead of the user's
      // selection. Both dayjs and moment expose `.format()` + `.isSame()`,
      // so use the picker's own objects directly without rewrapping.
      const start = val[0];
      const end = val[1];
      const startValid = start && typeof start.format === "function";
      const endValid = end && typeof end.format === "function";
      if (startValid && endValid) {
        const sameDay =
          typeof start.isSame === "function"
            ? start.isSame(end, "day")
            : false;
        return sameDay
          ? start.format(DATE_DISPLAY_FMT)
          : `${start.format(DATE_DISPLAY_FMT)} to ${end.format(
              DATE_DISPLAY_FMT
            )}`;
      }
    }

    if (typeof val === "string" && /\bto\b/i.test(val)) {
      const [lhs, rhs] = val.split(/\bto\b/i).map((s) => s.trim());
      const start = moment(lhs);
      const end = moment(rhs);
      if (start.isValid() && end.isValid()) {
        return start.isSame(end, "day")
          ? start.format(DATE_DISPLAY_FMT)
          : `${start.format(DATE_DISPLAY_FMT)} to ${end.format(
              DATE_DISPLAY_FMT
            )}`;
      }
    }

    const single = moment(val);
    if (single.isValid()) return single.format(DATE_DISPLAY_FMT);

    return val;
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      // FastAPI rows expose `id` as the PK; legacy Frappe rows used `name`.
      // Accept either so Select-All works across both.
      const allClientIds = data
        .map((row) => row.id || row.name)
        .filter(Boolean);
      setSelectedIds(allClientIds);
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectClient = (clientId) => {
    setSelectedIds((prev) =>
      prev.includes(clientId)
        ? prev.filter((id) => id !== clientId)
        : [...prev, clientId]
    );
  };

  const handleFilterChange = (columnKey, value) => {
    handleFilterValueChange(columnKey, value);
    setOpenFilterDropdown(null);
  };

  const clearAllFilters = () => {
    Object.keys(activeFilters).forEach((key) => {
      handleFilterValueChange(key, "");
    });
    setOpenFilterDropdown(null);
  };

  const clearSpecificFilter = (columnKey) => {
    handleFilterValueChange(columnKey, "");
  };

  const toggleColumnVisibility = (columnKey) => {
    setHiddenColumns((prev) =>
      prev.includes(columnKey)
        ? prev.filter((key) => key !== columnKey)
        : [...prev, columnKey]
    );
  };

  const handleCSVExport = async () => {
    let exportData = data;
    let isAll = false;
    if (onFetchAllRecords) {
      setIsExportingAll(true);
      try {
        exportData = await onFetchAllRecords();
        isAll = true;
      } catch (e) {
        setIsExportingAll(false);
        return;
      }
      setIsExportingAll(false);
    }

    const visibleColumns = columns.filter(
      (col) => !hiddenColumns.includes(col.key)
    );
    const headers = visibleColumns.map((col) => col.label).join(",");

    const csvData = exportData
      .map((row) => {
        return visibleColumns
          .map((col) => {
            const value = row[col.key] || "";
            return typeof value === "string" &&
              (value.includes(",") || value.includes('"'))
              ? `"${value.replace(/"/g, '""')}"`
              : value;
          })
          .join(",");
      })
      .join("\n");

    const csv = headers + "\n" + csvData;

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        isAll ? `${title}.csv` : `${title}_page_${currentPage}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    setShowExportModal(false);
  };

  const handlePDFExport = async () => {
    // jspdf is loaded on demand: TableV2 renders on every list page, so a
    // static import dragged ~350 KB into the main bundle for a button most
    // sessions never click (2026-08-19 bundle diet).
    const { default: jsPDF } = await import("jspdf");
    let exportData = data;
    let isAll = false;
    if (onFetchAllRecords) {
      setIsExportingAll(true);
      try {
        exportData = await onFetchAllRecords();
        isAll = true;
      } catch (e) {
        setIsExportingAll(false);
        return;
      }
      setIsExportingAll(false);
    }

    const pdf = new jsPDF("l", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 14;
    const contentWidth = pageWidth - margin * 2;

    let yPosition = 20;

    const checkPageBreak = (neededHeight) => {
      if (yPosition + neededHeight > pageHeight - 20) {
        pdf.addPage();
        yPosition = 20;
        return true;
      }
      return false;
    };

    const wrapText = (text, maxWidth) => {
      return pdf.splitTextToSize(text, maxWidth);
    };

    pdf.setFontSize(18);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(0, 0, 0);
    pdf.text(title, margin, yPosition);
    yPosition += 15;

    pdf.setFontSize(10);
    pdf.setFont("helvetica", "normal");
    const currentDate = moment().format("DD/MM/YYYY HH:mm");
    pdf.text(`Exported on: ${currentDate}`, margin, yPosition);
    yPosition += 5;
    pdf.text(`Page: ${currentPage} of ${totalPages}`, margin, yPosition);
    yPosition += 5;
    pdf.text(`Total Records: ${totalRecords}`, margin, yPosition);
    yPosition += 15;

    const visibleColumns = columns.filter(
      (col) => !hiddenColumns.includes(col.key)
    );

    const columnCount = visibleColumns.length;
    const baseColumnWidth = contentWidth / columnCount;
    const columnWidths = visibleColumns.map(() => baseColumnWidth);

    checkPageBreak(15);

    let xPosition = margin;
    const headerHeight = 12;

    visibleColumns.forEach((col, index) => {
      const colWidth = columnWidths[index];

      pdf.setFillColor(59, 130, 246);
      pdf.rect(
        xPosition,
        yPosition - headerHeight + 2,
        colWidth,
        headerHeight,
        "F"
      );

      pdf.setTextColor(255, 255, 255);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9);

      const headerText = wrapText(col.label, colWidth - 4);
      const textY = yPosition - headerHeight / 2 + 2;

      if (Array.isArray(headerText)) {
        headerText.forEach((line, lineIndex) => {
          pdf.text(line, xPosition + 2, textY + lineIndex * 3);
        });
      } else {
        pdf.text(headerText, xPosition + 2, textY);
      }

      pdf.setDrawColor(200, 200, 200);
      pdf.rect(xPosition, yPosition - headerHeight + 2, colWidth, headerHeight);

      xPosition += colWidth;
    });

    yPosition += 5;

    exportData.forEach((row, rowIndex) => {
      const rowHeight = 10;
      checkPageBreak(rowHeight + 5);

      xPosition = margin;

      const isEvenRow = rowIndex % 2 === 0;
      const bgColor = isEvenRow ? [255, 255, 255] : [248, 250, 252];

      visibleColumns.forEach((col, colIndex) => {
        const colWidth = columnWidths[colIndex];

        let cellValue = row[col.key || col.filterName] || "";

        if (typeof cellValue === "object" && cellValue !== null) {
          if (col.render) {
            const renderedContent = col.render(row);
            if (typeof renderedContent === "string") {
              cellValue = renderedContent;
            } else {
              cellValue = String(cellValue);
            }
          } else {
            cellValue = JSON.stringify(cellValue);
          }
        }

        cellValue = String(cellValue);

        if (cellValue.length > 50) {
          cellValue = cellValue.substring(0, 47) + "...";
        }

        pdf.setFillColor(...bgColor);
        pdf.rect(
          xPosition,
          yPosition - rowHeight + 2,
          colWidth,
          rowHeight,
          "F"
        );

        pdf.setTextColor(0, 0, 0);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8);

        const wrappedText = wrapText(cellValue, colWidth - 4);
        const textY = yPosition - rowHeight / 2 + 1;

        if (Array.isArray(wrappedText)) {
          wrappedText.forEach((line, lineIndex) => {
            if (lineIndex < 2) {
              pdf.text(line, xPosition + 2, textY + lineIndex * 3);
            }
          });
        } else {
          pdf.text(wrappedText, xPosition + 2, textY);
        }

        pdf.setDrawColor(200, 200, 200);
        pdf.setLineWidth(0.1);
        pdf.rect(xPosition, yPosition - rowHeight + 2, colWidth, rowHeight);

        xPosition += colWidth;
      });

      yPosition += rowHeight + 2;
    });

    const activeFilterCount = Object.keys(activeFilters).filter(
      (key) =>
        activeFilters[key] &&
        activeFilters[key] !== "" &&
        activeFilters[key] !== null &&
        !(
          Array.isArray(activeFilters[key]) &&
          activeFilters[key].every((v) => !v)
        )
    ).length;

    if (activeFilterCount > 0) {
      yPosition += 10;
      checkPageBreak(20);

      pdf.setFontSize(12);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(0, 0, 0);
      pdf.text("Applied Filters:", margin, yPosition);
      yPosition += 10;

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9);

      Object.entries(activeFilters).forEach(([key, value]) => {
        if (
          value &&
          value !== "" &&
          value !== null &&
          !(Array.isArray(value) && value.every((v) => !v))
        ) {
          checkPageBreak(8);

          const column = columns.find((col) => col.filterName === key);
          let displayValue = value;

          if (Array.isArray(value) && value.length === 2) {
            // Use the picker's date objects directly — same dayjs/moment
            // unwrap fix as in formatDateOrRange above (moment-wrapping a
            // dayjs object silently coerces to today).
            const v0 = value[0];
            const v1 = value[1];
            const v0Fmt =
              v0 && typeof v0.format === "function"
                ? v0.format("DD/MM/YYYY")
                : String(v0);
            const v1Fmt =
              v1 && typeof v1.format === "function"
                ? v1.format("DD/MM/YYYY")
                : String(v1);
            displayValue = `${v0Fmt} to ${v1Fmt}`;
          }

          pdf.setFont("helvetica", "bold");
          pdf.text(`${column?.label || key}: `, margin, yPosition);

          pdf.setFont("helvetica", "normal");
          const labelWidth = pdf.getTextWidth(`${column?.label || key}: `);
          pdf.text(String(displayValue), margin + labelWidth, yPosition);

          yPosition += 6;
        }
      });
    }

    const totalPDFPages = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= totalPDFPages; i++) {
      pdf.setPage(i);
      pdf.setFontSize(8);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(100, 100, 100);

      const pageText = `Page ${i} of ${totalPDFPages}`;
      const footerY = pageHeight - 10;
      pdf.text(pageText, margin, footerY);

      const timestamp = moment().format("DD/MM/YYYY HH:mm");
      const timestampWidth = pdf.getTextWidth(timestamp);
      pdf.text(timestamp, pageWidth - margin - timestampWidth, footerY);
    }

    const filename = isAll
      ? `${title.replace(/\s+/g, "_")}.pdf`
      : `${title.replace(/\s+/g, "_")}_page_${currentPage}.pdf`;
    pdf.save(filename);
    setShowExportModal(false);
  };

  const goToNextPage = () => {
    const nextLimitStart = limitStart + limit;
    onPageChange(nextLimitStart, limit);
  };

  const goToPrevPage = () => {
    const prevLimitStart = Math.max(limitStart - limit, 0);
    onPageChange(prevLimitStart, limit);
  };

  const ColumnSettingsDropdown = ({ isOpen, onClose }) => {
    if (!isOpen) return null;

    return (
      <div className="absolute top-full right-0 mt-1 w-64 bg-white border border-gray-200 rounded-xl shadow-2xl z-[99999] animate-in fade-in duration-200">
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-gray-800 text-sm">
              Column Visibility
            </h4>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-3 h-3 text-gray-400" />
            </button>
          </div>

          <div className="space-y-2 max-h-64 overflow-y-auto">
            {columns.map((column) => (
              <div
                key={column.filterName}
                className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <span className="text-sm text-gray-700">{column.label}</span>
                <button
                  onClick={() => toggleColumnVisibility(column.filterName)}
                  className="p-1 hover:bg-gray-200 rounded transition-colors"
                >
                  {hiddenColumns.includes(column.filterName) ? (
                    <EyeOff className="w-4 h-4 text-gray-400" />
                  ) : (
                    <Eye className="w-4 h-4 text-blue-600" />
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7; // Show max 7 page numbers

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(
          <button
            key={i}
            onClick={() => onPageChange((i - 1) * limit, limit)}
            className={`
            w-10 h-10 rounded-lg text-sm font-medium transition-all duration-200
            ${
              currentPage === i
                ? "bg-blue-600 text-white shadow-lg shadow-blue-600/30 scale-110"
                : "bg-white text-gray-700 hover:bg-blue-50 hover:text-blue-600 border border-gray-200 hover:border-blue-300 shadow-sm hover:shadow-md"
            }
          `}
          >
            {i}
          </button>
        );
      }
    } else {
      // Always show first page
      pages.push(
        <button
          key={1}
          onClick={() => onPageChange(0, limit)}
          className={`
          w-10 h-10 rounded-lg text-sm font-medium transition-all duration-200
          ${
            currentPage === 1
              ? "bg-blue-600 text-white shadow-lg shadow-blue-600/30 scale-110"
              : "bg-white text-gray-700 hover:bg-blue-50 hover:text-blue-600 border border-gray-200 hover:border-blue-300 shadow-sm hover:shadow-md"
          }
        `}
        >
          1
        </button>
      );

      // Show ellipsis after first page if current page is far from start
      if (currentPage > 3) {
        pages.push(
          <span
            key="ellipsis-start"
            className="w-10 h-10 flex items-center justify-center text-gray-400 font-medium"
          >
            ...
          </span>
        );
      }

      // Show pages around current page
      const startPage = Math.max(2, currentPage - 1);
      const endPage = Math.min(totalPages - 1, currentPage + 1);

      for (let i = startPage; i <= endPage; i++) {
        pages.push(
          <button
            key={i}
            onClick={() => onPageChange((i - 1) * limit, limit)}
            className={`
            w-10 h-10 rounded-lg text-sm font-medium transition-all duration-200
            ${
              currentPage === i
                ? "bg-blue-600 text-white shadow-lg shadow-blue-600/30 scale-110"
                : "bg-white text-gray-700 hover:bg-blue-50 hover:text-blue-600 border border-gray-200 hover:border-blue-300 shadow-sm hover:shadow-md"
            }
          `}
          >
            {i}
          </button>
        );
      }

      // Show ellipsis before last page if current page is far from end
      if (currentPage < totalPages - 2) {
        pages.push(
          <span
            key="ellipsis-end"
            className="w-10 h-10 flex items-center justify-center text-gray-400 font-medium"
          >
            ...
          </span>
        );
      }

      // Always show last page
      pages.push(
        <button
          key={totalPages}
          onClick={() => onPageChange((totalPages - 1) * limit, limit)}
          className={`
          w-10 h-10 rounded-lg text-sm font-medium transition-all duration-200
          ${
            currentPage === totalPages
              ? "bg-blue-600 text-white shadow-lg shadow-blue-600/30 scale-110"
              : "bg-white text-gray-700 hover:bg-blue-50 hover:text-blue-600 border border-gray-200 hover:border-blue-300 shadow-sm hover:shadow-md"
          }
        `}
        >
          {totalPages}
        </button>
      );
    }

    return pages;
  };

  const ExportModal = ({ isOpen, onClose }) => {
    if (!isOpen) return null;

    return (
      <>
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[99998] animate-in fade-in duration-200"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="fixed inset-0 flex items-center justify-center z-[99999] p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md animate-in zoom-in duration-200">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-800">Export Data</h3>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Choose your preferred export format
              </p>
            </div>

            {/* Content */}
            <div className="p-6 space-y-3">
              {/* PDF Export Option */}
              <button
                onClick={handlePDFExport}
                className="w-full flex items-center gap-4 p-4 bg-gradient-to-r from-red-50 to-pink-50 border-2 border-red-200 rounded-xl hover:border-red-400 hover:shadow-lg transition-all duration-200 group"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-red-500 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 text-left">
                  <h4 className="font-semibold text-gray-800 text-base">
                    Download as PDF
                  </h4>
                  <p className="text-sm text-gray-600">
                    {onFetchAllRecords
                      ? "Export all records in PDF format"
                      : `Export page ${currentPage} in PDF format`}
                  </p>
                </div>
                <Download className="w-5 h-5 text-red-500 group-hover:translate-x-1 transition-transform" />
              </button>

              {/* CSV Export Option */}
              <button
                onClick={handleCSVExport}
                className="w-full flex items-center gap-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl hover:border-green-400 hover:shadow-lg transition-all duration-200 group"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <FileSpreadsheet className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 text-left">
                  <h4 className="font-semibold text-gray-800 text-base">
                    Download as CSV
                  </h4>
                  <p className="text-sm text-gray-600">
                    {onFetchAllRecords
                      ? "Export all records in CSV format"
                      : `Export page ${currentPage} in CSV format`}
                  </p>
                </div>
                <Download className="w-5 h-5 text-green-500 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 rounded-b-2xl border-t border-gray-200">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                <span>
                  {onFetchAllRecords
                    ? `Exporting all ${totalRecords} records (matching current filters)`
                    : `Exporting ${data.length} records from page ${currentPage}`}
                </span>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  const activeFilterCount = Object.keys(activeFilters).filter(
    (key) =>
      activeFilters[key] &&
      activeFilters[key] !== "" &&
      activeFilters[key] !== null &&
      !(
        Array.isArray(activeFilters[key]) && activeFilters[key].every((v) => !v)
      )
  ).length;

  const hasSelection = selectedIds.length > 0;
  const isAllSelected = selectedIds.length === data.length && data.length > 0;
  const isPartiallySelected =
    selectedIds.length > 0 && selectedIds.length < data.length;

  const visibleColumns = columns.filter(
    (col) => !hiddenColumns.includes(col.key)
  );

  return (
    <div className="w-full bg-white shadow-2xl rounded-2xl overflow-hidden border border-gray-100">
      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
      />

      {/* Advanced Header Section */}
      <div className="bg-gradient-to-r from-slate-50 via-gray-50 to-slate-50 border-b border-gray-200">
        {/* Top Header Bar */}
        <div className="px-6 py-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                  <div className="w-5 h-5 bg-white rounded-md opacity-90"></div>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-800">{title}</h2>
                  <p className="text-sm text-gray-500">
                    {totalRecords} records • {columns.length} columns
                  </p>
                </div>
              </div>

              {/* Active Filters */}
              {activeFilterCount > 0 && (
                <div className="flex items-center gap-2">
                  <div className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium flex items-center gap-1">
                    <Filter className="w-3 h-3" />
                    {activeFilterCount} filter{activeFilterCount > 1 ? "s" : ""}
                  </div>
                  <button
                    onClick={clearAllFilters}
                    className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm hover:bg-gray-200 transition-colors"
                  >
                    Clear all
                  </button>
                </div>
              )}
            </div>

            {/* Optional CENTER slot — fills the empty space between the title
                block and the action buttons. Used by the grouped Income-Tax
                e-proceedings screen to show which group is open. Nothing
                renders (and the layout is unchanged) when it isn't passed. */}
            {headerCenter && (
              <div className="flex-1 flex justify-center px-4 min-w-0">
                {headerCenter}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              {hasSelection && (
                <div className="flex items-center gap-3 mr-4 px-4 py-2 bg-blue-50 rounded-lg border border-blue-200">
                  <span className="text-sm text-blue-700 font-medium">
                    {selectedIds.length} selected
                  </span>
                  <button
                    onClick={() => setSelectedIds([])}
                    className="text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* DEPRECATED legacy export (CSV/PDF reading flat row[col.key],
                  leaving rendered columns empty). Hidden in favour of the
                  unified <DownloadDataButton/> in each list page's actions
                  row. Gated off by default; pass showLegacyExport to re-enable
                  during transition. Remove this block + handleCSVExport /
                  handlePDFExport / ExportModal once the new flow is signed off. */}
              {showLegacyExport && (
                <button
                  onClick={() => setShowExportModal(true)}
                  className="p-2.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all duration-200 group"
                  title="Export Data"
                >
                  <Download className="w-4 h-4 group-hover:scale-110 transition-transform" />
                </button>
              )}

              {/* <div className="relative">
                <button
                  onClick={() => setShowColumnSettings(!showColumnSettings)}
                  className="p-2.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all duration-200 group"
                  title="Column Settings"
                >
                  <Settings className="w-4 h-4 group-hover:rotate-90 transition-transform duration-300" />
                </button>
                <ColumnSettingsDropdown
                  isOpen={showColumnSettings}
                  onClose={() => setShowColumnSettings(false)}
                />
              </div> */}
            </div>
          </div>
        </div>

        {/* Active Filters Display */}
        {activeFilterCount > 0 && (
          <div className="px-6 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-gray-600 font-medium">
                Active filters:
              </span>
              {Object.entries(activeFilters).map(([key, value]) => {
                if (
                  !value ||
                  value === "" ||
                  value === null ||
                  (Array.isArray(value) && value.every((v) => !v))
                )
                  return null;

                const column = columns.find((col) => col.filterName === key);
                let displayValue = value;

                if (Array.isArray(value) && value.length === 2) {
                  displayValue = formatDateOrRange(value);
                }

                return (
                  <div
                    key={key}
                    className="flex items-center gap-2 px-3 py-1 bg-white border border-blue-200 rounded-lg text-sm"
                  >
                    <span className="font-medium text-gray-700">
                      {column?.label}:
                    </span>
                    <span className="text-gray-600">{displayValue}</span>
                    <button
                      onClick={() => clearSpecificFilter(key)}
                      className="ml-1 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      {/* Table Section */}
      <div className="overflow-x-auto">
        <table className="w-full" style={{ minHeight: "38vh" }}>
          <thead className="bg-gradient-to-r from-gray-50 to-slate-50 border-b border-gray-200">
            <tr>
              {/* Enhanced Selection Column */}
              {(type === "clients" ||
                type === "eproceeding" ||
                type === "responseoutstandings" ||
                type === "notices") && (
                <th className="p-4 text-left w-16 bg-gray-50/50">
                  <div className="flex items-center justify-center">
                    <div className="relative">
                      <input
                        type="checkbox"
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                        onChange={handleSelectAll}
                        checked={isAllSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = isPartiallySelected;
                        }}
                      />
                      {isPartiallySelected && (
                        <Minus className="absolute inset-0 w-4 h-4 text-blue-600 pointer-events-none" />
                      )}
                    </div>
                  </div>
                </th>
              )}

              {/* Enhanced Data Columns */}
              {visibleColumns.map((column, index) => (
                <th
                  key={index}
                  className="p-4 text-left min-w-[200px] relative group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 justify-center text-center w-full">
                      <span className="font-bold text-gray-700 text-sm tracking-wide text-center">
                        {column.label}
                      </span>
                    </div>

                    {/* Column Actions - Filter Always Visible */}
                    <div className="flex items-center gap-1">
                      {column.filterable !== false && (
                        <div className="relative">
                          <button
                            data-filter-button
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenFilterDropdown(
                                openFilterDropdown === column.filterName
                                  ? null
                                  : column.filterName
                              );
                            }}
                            className={`p-1.5 rounded-lg transition-colors ${
                              activeFilters[column.filterName] &&
                              activeFilters[column.filterName] !== "" &&
                              !(
                                Array.isArray(
                                  activeFilters[column.filterName]
                                ) &&
                                activeFilters[column.filterName].every(
                                  (v) => !v
                                )
                              )
                                ? "bg-blue-100 text-blue-600"
                                : "text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                            }`}
                            title="Filter column"
                          >
                            <Filter className="w-3.5 h-3.5" />
                          </button>
                          <FilterDropdown
                            column={column}
                            isOpen={openFilterDropdown === column.filterName}
                            onClose={() => setOpenFilterDropdown(null)}
                            columnIndex={index}
                            totalColumns={visibleColumns.length}
                            activeFilters={activeFilters}
                            handleFilterChange={handleFilterChange}
                            serviceType={serviceType}
                            statusOptions={statusOptions}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading ? (
              <tr>
                <td
                  colSpan={visibleColumns.length + 1}
                  className="p-16 text-center"
                >
                  <div className="flex flex-col items-center justify-center space-y-4">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    <p className="text-gray-500 text-lg">Loading data...</p>
                  </div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td
                  colSpan={visibleColumns.length + 1}
                  className="p-16 text-center"
                >
                  <div className="flex flex-col items-center justify-center space-y-4">
                    <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center">
                      <div className="w-10 h-10 bg-gray-300 rounded-full"></div>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xl font-medium">
                        No data available
                      </p>
                      <p className="text-gray-400 text-sm mt-1">
                        Try adjusting your filters or refresh the data
                      </p>
                    </div>
                  </div>
                </td>
              </tr>
            ) : (
              data.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className={`
                    group transition-all duration-300 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50
                    ${
                      selectedIds.includes(row.id || row.name)
                        ? "bg-blue-50/70 border-l-4 border-blue-400"
                        : ""
                    }
                    ${
                      hoveredRow === rowIndex
                        ? "shadow-lg transform scale-[1.01]"
                        : ""
                    }
                  `}
                  onMouseEnter={() => {
                    if (!openFilterDropdown) {
                      setHoveredRow(rowIndex);
                    }
                  }}
                  onMouseLeave={() => {
                    if (!openFilterDropdown) {
                      setHoveredRow(null);
                    }
                  }}
                >
                  {/* Enhanced Selection Column */}
                  {(type === "clients" ||
                    type === "eproceeding" ||
                    type === "responseoutstandings" ||
                    type === "notices") && (
                    <td className="p-4">
                      <div className="flex items-center justify-center">
                        <input
                          type="checkbox"
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={type === "clients" && !!row?.disabled}
                          checked={selectedIds.includes(row.id || row.name)}
                          onChange={() =>
                            handleSelectClient(row.id || row.name)
                          }
                        />
                      </div>
                    </td>
                  )}

                  {/* Enhanced Data Columns */}
                  {visibleColumns.map((column, colIndex) => (
                    <td
                      key={colIndex}
                      className="p-4 cursor-pointer transition-all duration-200 group-hover:text-blue-600 "
                      onClick={(e) => {
                        if (column.preventRowClick) {
                          e.stopPropagation();
                          return;
                        }
                        handleRowClick(row);
                      }}
                    >
                      <div className="flex items-center  justify-center w-full">
                        {column.render ? (
                          <div className="w-full">{column.render(row)}</div>
                        ) : (
                          <span
                            className={`text-sm text-gray-700 font-medium group-hover:font-semibold transition-all text-center ${
                              column.filterable ? "mr-5" : ""
                            }`}
                          >
                            {row[column.key || column.filterName]}
                          </span>
                        )}
                      </div>
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {/* Enhanced Pagination */}
      {isPagination && totalRecords > 0 && (
        <div className="bg-gradient-to-r from-gray-50 to-slate-50 px-6 py-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-600 bg-white px-3 py-1 rounded-full border border-gray-200">
                Showing{" "}
                <span className="font-semibold text-gray-800">
                  {limitStart + 1}
                </span>{" "}
                to{" "}
                <span className="font-semibold text-gray-800">
                  {Math.min(limitStart + limit, totalRecords)}
                </span>{" "}
                of{" "}
                <span className="font-semibold text-gray-800">
                  {totalRecords}
                </span>{" "}
                entries
              </div>
              {hasSelection && (
                <div className="text-sm text-blue-600 font-medium bg-blue-50 px-3 py-1 rounded-full border border-blue-200">
                  {selectedIds.length} selected
                </div>
              )}
            </div>

            <div className="flex items-center justify-between mt-6">
              <div className="flex items-center gap-2">
                <button
                  onClick={goToPrevPage}
                  disabled={currentPage === 1}
                  className={`
        px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 border
        ${
          currentPage === 1
            ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
            : "bg-white text-gray-700 hover:bg-blue-50 hover:text-blue-600 border-gray-200 hover:border-blue-300 shadow-sm hover:shadow-md"
        }
      `}
                >
                  Previous
                </button>

                <div className="flex items-center gap-1">
                  {renderPageNumbers()}
                </div>

                <button
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages || totalPages === 0}
                  className={`
        px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 border
        ${
          currentPage === totalPages || totalPages === 0
            ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
            : "bg-white text-gray-700 hover:bg-blue-50 hover:text-blue-600 border-gray-200 hover:border-blue-300 shadow-sm hover:shadow-md"
        }
      `}
                >
                  Next
                </button>
              </div>

              {/* Right side: page-length selector + View All + page info */}
              <div className="flex items-center gap-4 ml-4">
                {onLimitChange && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Show</span>
                    <select
                      value={limit}
                      onChange={(e) => onLimitChange(Number(e.target.value))}
                      className="border border-gray-300 rounded-lg px-2 py-1 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-300"
                    >
                      {[20, 50, 100].map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                      {/* Show the current custom size (e.g. after View All)
                          so the select doesn't render a blank value. */}
                      {![20, 50, 100].includes(limit) && (
                        <option value={limit}>{limit}</option>
                      )}
                    </select>
                    <span className="text-sm text-gray-600">per page</span>
                    <button
                      onClick={() =>
                        onLimitChange(Math.max(totalRecords, 1))
                      }
                      disabled={limit >= totalRecords && totalRecords > 0}
                      className={`px-3 py-1 rounded-lg text-sm font-medium border transition-all duration-200 ${
                        limit >= totalRecords && totalRecords > 0
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                          : "bg-white text-blue-600 hover:bg-blue-50 border-blue-200 hover:border-blue-300 shadow-sm"
                      }`}
                    >
                      View All
                    </button>
                  </div>
                )}
                <div className="text-sm text-gray-600 font-medium">
                  Page {currentPage} of {totalPages}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TableV2;
